import { memo, useState, useMemo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { cn } from "@/shared/utils/general/cn"
import type { DbObject } from "@/db/schema"
import { db } from "@/db/schema"
import { useLiveQueryImmediate } from "@/db/hooks"
import { moveTask } from "@/db/operations"
import { ContainerPicker } from "./ContainerPicker"
import InboxIcon from "@/shared/icons/pikaicons/stroke/communication/inbox-default.svg?react"

interface DetailContainerProps {
  task: DbObject
}

/** Virtual container IDs that don't exist as DB objects */
const VIRTUAL_CONTAINERS: Record<string, string> = {
  "#inbox": "Inbox",
  "#anyday": "Anyday",
  "#someday": "Someday",
  "#today": "Today",
}

/**
 * Extract the parent container ID from a parentPath.
 * parentPath format: "root/projectId" → last segment = projectId
 */
function getParentId(parentPath: string): string | null {
  if (!parentPath) return null
  const segments = parentPath.split("/").filter(Boolean)
  return segments.length > 0 ? (segments[segments.length - 1] ?? null) : null
}

/**
 * Clickable container label that opens ContainerPicker for reassignment.
 * Shown in TaskDetail on views where task container isn't implicit (e.g. /recurring).
 */
export const DetailContainer = memo(function DetailContainer({
  task,
}: DetailContainerProps) {
  const { t } = useTranslation()
  const [pickerOpen, setPickerOpen] = useState(false)

  const parentId = useMemo(
    () => getParentId(task.parentPath),
    [task.parentPath],
  )

  const [parent] = useLiveQueryImmediate(
    () => (parentId && !parentId.startsWith("#") ? db.totodo.get(parentId) : undefined),
    ["detail-container", parentId],
    undefined,
  )

  const handleSelect = useCallback(
    async (targetId: string) => {
      if (!task.id) return
      const newParentPath = `root/${targetId}`
      const result = await moveTask(task.id, newParentPath)
      // A refused move moved nothing: say why instead of closing the picker as if it had worked.
      if (!result.ok) toast.error(result.error)
    },
    [task.id],
  )

  const containerTitle = (parentId && VIRTUAL_CONTAINERS[parentId]) ?? parent?.title ?? t("Inbox")

  return (
    <>
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        className={cn(
          "inline-flex min-h-8 items-center gap-1.5",
          "text-sm text-muted-foreground/70 hover:text-foreground/90",
          "cursor-pointer transition-opacity hover:opacity-70",
        )}
      >
        <InboxIcon className="size-5" />
        <span className="truncate max-w-32">{containerTitle}</span>
      </button>

      <ContainerPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={handleSelect}
        currentContainerId={parentId ?? undefined}
        showVirtualContainers
      />
    </>
  )
})
